import React, { useState, useEffect, useMemo } from 'react';
import { User, UserCreateData } from '../../services/userManagementService';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Select from '../UI/Select';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../UI/PhoneInputWithValidation';
import { parseE164Phone } from '../../utils/phoneValidationV2';
import { findCountryByCode } from '../../data/countryCodes';
import { Shield, User as UserIcon, Users, UserCheck, CheckCircle, XCircle } from 'lucide-react';

interface UserFormProps {
  user?: User;
  onSubmit: (userData: UserCreateData | Partial<UserCreateData>) => void;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
  serverErrors?: Record<string, string>;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  loading = false,
  isEdit = false,
  serverErrors = {}
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [formData, setFormData] = useState<UserCreateData>({
    email: '',
    phone: '',
    title: '',
    first_name: '',
    last_name: '',
    role: 'guest',
    roles: ['guest'], // Initialize with default role
    is_active: true,
    password: '',
    password_confirm: ''
  });
  const [countryCode, setCountryCode] = useState<string>('JO');
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [customTitle, setCustomTitle] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Track which fields have been edited to clear server errors
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());

  // Merge server errors with local errors, but exclude server errors for edited fields
  const allErrors = useMemo(() => {
    return { 
      ...errors, 
      ...Object.fromEntries(
        Object.entries(serverErrors).filter(([key]) => !editedFields.has(key))
      )
    };
  }, [errors, serverErrors, editedFields]);

  // Clear editedFields when new server errors arrive so they can be displayed
  useEffect(() => {
    if (Object.keys(serverErrors).length > 0) {
      // Clear editedFields for fields that have server errors
      // This allows server errors to be displayed even if the user has edited those fields
      setEditedFields(prev => {
        const newSet = new Set(prev);
        // Remove fields that have server errors from editedFields
        Object.keys(serverErrors).forEach(key => {
          newSet.delete(key);
        });
        return newSet;
      });
    }
  }, [serverErrors]);

  // Initialize form data when editing
  useEffect(() => {
    if (user && isEdit) {
      // Get user roles - allow admins to have multiple roles
      const userRoles = user.roles || [user.role];
      
      // Parse existing E.164 phone to get local number and country
      let phoneValue = user.phone || '';
      let countryCodeValue = 'JO';
      
      if (user.phone) {
        try {
          const { localNumber, countryCode: detectedCountry } = parseE164Phone(user.phone);
          phoneValue = localNumber;
          const country = findCountryByCode(detectedCountry);
          if (country) {
            countryCodeValue = country.code;
          }
        } catch (error) {
          // If parsing fails, use phone as-is
          phoneValue = user.phone;
        }
      }
      
      setFormData({
        email: user.email || '',
        phone: phoneValue,
        title: user.title || '',
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        roles: userRoles, // Allow admins to have multiple roles
        is_active: user.is_active,
        password: '',
        password_confirm: ''
      });
      
      setCountryCode(countryCodeValue);
    }
  }, [user, isEdit]);

  // Reset edited fields when user changes or modal opens
  useEffect(() => {
    setEditedFields(new Set());
    setErrors({});
  }, [user, isEdit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Mark field as edited to clear server errors
    setEditedFields(prev => new Set(prev).add(name));

    // Clear error when user starts typing
    if (allErrors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    // Validate roles array
    const selectedRoles = formData.roles || [];
    if (selectedRoles.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    // Role-based email/phone requirements
    // Check if any selected role requires email or phone
    const requiresEmail = selectedRoles.some(role => role === 'host' || role === 'admin');
    const requiresPhone = selectedRoles.some(role => role === 'guest' || role === 'usher');

    // Email validation
    if (requiresEmail) {
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'Email is required for ' + (formData.role === 'host' ? 'host' : 'admin') + ' users';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      // Email is optional but must be valid if provided
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation - use backend validation result
    if (requiresPhone) {
      if (!formData.phone || !formData.phone.trim()) {
        newErrors.phone = 'Phone number is required for ' + (formData.role === 'guest' ? 'guest' : 'usher') + ' users';
      } else if (!phoneValidation || !phoneValidation.valid) {
        newErrors.phone = phoneValidation?.error || 'Please enter a valid phone number';
      }
    } else if (formData.phone && formData.phone.trim()) {
      // Phone is optional but must be valid if provided
      if (!phoneValidation || !phoneValidation.valid) {
        newErrors.phone = phoneValidation?.error || 'Please enter a valid phone number';
      }
    }

    // Password validation (only for new users)
    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      // Password confirmation
      if (formData.password && formData.password !== formData.password_confirm) {
        newErrors.password_confirm = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const isValid = validateForm();
    
    if (!isValid) {
      return;
    }

    // Clear any existing server errors since validation passed
    setErrors({});
    
    // Prepare data for submission
    let submitData: UserCreateData | Partial<UserCreateData> = { ...formData };
    
    // Get the title value (use custom title if "Other" is selected)
    if (formData.title === 'Other' && customTitle.trim()) {
      submitData.title = customTitle.trim();
    } else if (formData.title === 'Other') {
      // If Other is selected but no custom title provided, don't include title
      delete submitData.title;
    } else if (!formData.title || formData.title.trim() === '') {
      // If no title is selected, don't include it
      delete submitData.title;
    }
    
    // Ensure roles array is included in submission
    if (formData.roles && formData.roles.length > 0) {
      submitData.roles = formData.roles;
      // Set primary role to first role in array
      submitData.role = formData.roles[0] as 'admin' | 'host' | 'guest' | 'usher';
    }
    
    // Use normalized phone from validation if available, otherwise use formData.phone
    if (phoneValidation && phoneValidation.valid) {
      submitData.phone = phoneValidation.normalized;
      (submitData as any).country_code = phoneValidation.country;
    } else {
      // Fallback: use formData.phone and countryCode state
      (submitData as any).country_code = countryCode;
    }
    
    // Remove password fields for edit mode (passwords are not editable)
    if (isEdit) {
      const { password, password_confirm, ...rest } = submitData;
      submitData = rest;
    }

    onSubmit(submitData);
  };



  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4 [&_label]:min-h-[1.25rem] [&_label]:text-sm [&_label]:leading-tight [&_label]:block">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Basic Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4 items-start">
          <div className="w-full min-w-0 flex flex-col">
            <Select
              options={[
                { value: 'Dr', label: 'Dr' },
                { value: 'Mr', label: 'Mr' },
                { value: 'Ms', label: 'Ms' },
                { value: 'Mrs', label: 'Mrs' },
                { value: 'Prof', label: 'Prof' },
                { value: 'Eng', label: 'Eng' },
                { value: 'Rev', label: 'Rev' },
                { value: 'Other', label: 'Other' }
              ]}
              value={formData.title || ''}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, title: value }));
                // Mark title as edited to clear server errors
                setEditedFields(prev => new Set(prev).add('title'));
                if (allErrors.title) {
                  setErrors(prev => ({ ...prev, title: '' }));
                }
              }}
              placeholder="Select Title"
              label="Title"
              className="w-full"
              buttonClassName="h-14"
            />
          </div>
          
          <div className="w-full min-w-0 flex flex-col">
            <Input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleInputChange}
              error={allErrors.first_name}
              placeholder="Enter first name"
              label="First Name"
              required
              inputSize="lg"
              className="w-full h-14"
            />
          </div>

          <div className="w-full min-w-0 flex flex-col">
            <Input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleInputChange}
              error={allErrors.last_name}
              placeholder="Enter last name"
              label="Last Name"
              required
              inputSize="lg"
              className="w-full h-14"
            />
          </div>
        </div>

        {/* Custom Title Input (shown when "Other" is selected) */}
        {formData.title === 'Other' && (
          <div className="w-full">
            <Input
              id="custom_title"
              name="custom_title"
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Enter custom title"
              label="Custom Title"
              required
              inputSize="lg"
              className="w-full h-14"
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 items-start">
          <div className="w-full min-w-0 flex flex-col">
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={allErrors.email}
              placeholder="Enter email address"
              label={`Email Address${(formData.role === 'host' || formData.role === 'admin') ? ' *' : ''}`}
              required={formData.role === 'host' || formData.role === 'admin'}
              inputSize="lg"
              className="w-full h-14"
            />
          </div>

          <div className="w-full min-w-0 flex flex-col">
            <label className={`block text-sm font-medium mb-2 min-h-[1.25rem] ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Phone Number{(formData.role === 'guest' || formData.role === 'usher') ? ' *' : ''}
            </label>
            <PhoneInputWithValidation
              value={formData.phone}
              onChange={(phoneValue, countryCodeValue) => {
                setFormData(prev => ({ ...prev, phone: phoneValue }));
                setCountryCode(countryCodeValue);
                // Mark phone as edited
                setEditedFields(prev => new Set(prev).add('phone'));
                if (allErrors.phone) {
                  setErrors(prev => ({ ...prev, phone: '' }));
                }
              }}
              onValidationChange={setPhoneValidation}
              error={allErrors.phone}
              required={formData.role === 'guest' || formData.role === 'usher'}
              showValidationResult={false}
              placeholder="Phone number"
              initialCountryCode={countryCode}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* User Roles */}
      <div className="space-y-4">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>User Roles *</h3>
        
        <div>
          <label className={`block text-sm font-medium mb-3 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Select one or more roles
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { 
                value: 'admin', 
                label: 'Admin', 
                description: 'Full system access with email/password login',
                icon: Shield,
                color: 'bg-red-50 border-red-200 text-red-700',
                darkColor: 'bg-red-900/20 border-red-800 text-red-400'
              },
              { 
                value: 'host', 
                label: 'Host', 
                description: 'Event creators with email/password login',
                icon: UserIcon,
                color: 'bg-blue-50 border-blue-200 text-blue-700',
                darkColor: 'bg-blue-900/20 border-blue-800 text-blue-400'
              },
              { 
                value: 'guest', 
                label: 'Guest', 
                description: 'Event attendees with phone/OTP login',
                icon: Users,
                color: 'bg-green-50 border-green-200 text-green-700',
                darkColor: 'bg-green-900/20 border-green-800 text-green-400'
              },
              { 
                value: 'usher', 
                label: 'Usher', 
                description: 'Event staff with phone/OTP login',
                icon: UserCheck,
                color: 'bg-purple-50 border-purple-200 text-purple-700',
                darkColor: 'bg-purple-900/20 border-purple-800 text-purple-400'
              }
            ].map((roleOption) => {
              const Icon = roleOption.icon;
              const isSelected = (formData.roles || []).includes(roleOption.value);
              
              // Allow all roles to be selected - admins can have multiple roles
              const isDisabled = false;
              
              return (
                <div
                  key={roleOption.value}
                  onClick={() => {
                    const currentRoles = formData.roles || [];
                    let newRoles: string[];
                    
                    if (roleOption.value === 'admin') {
                      // Allow admin to be combined with other roles
                      if (isSelected) {
                        // Deselecting admin - remove admin but keep other roles
                        newRoles = currentRoles.filter(r => r !== 'admin');
                      } else {
                        // Selecting admin - add admin to existing roles
                        newRoles = [...currentRoles, 'admin'];
                      }
                    } else {
                      // Normal role toggle - allow adding roles even if admin is selected
                      if (isSelected) {
                        newRoles = currentRoles.filter(r => r !== roleOption.value);
                      } else {
                        newRoles = [...currentRoles, roleOption.value];
                      }
                    }
                    
                    setFormData(prev => ({
                      ...prev,
                      roles: newRoles,
                      role: (newRoles.length > 0 
                        ? (newRoles.includes('admin') 
                            ? 'admin' 
                            : (newRoles[0] as 'admin' | 'host' | 'guest' | 'usher'))
                        : prev.role)
                    }));
                    // Mark roles as edited to clear server errors
                    setEditedFields(prev => new Set(prev).add('roles'));
                    if (allErrors.roles) {
                      setErrors(prev => ({ ...prev, roles: '' }));
                    }
                  }}
                  className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                    isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  } ${
                    isSelected
                      ? theme === 'dark'
                        ? `${roleOption.darkColor} border-mirsal-green ring-2 ring-mirsal-green/50`
                        : `${roleOption.color} border-mirsal-green ring-2 ring-mirsal-green/50`
                      : theme === 'dark'
                      ? isDisabled
                        ? 'border-gray-800 bg-gray-900/30'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                      : isDisabled
                        ? 'border-gray-300 bg-gray-100'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`flex-shrink-0 mt-0.5 ${
                      isSelected
                        ? theme === 'dark' ? 'text-mirsal-green' : 'text-mirsal-green'
                        : theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <label
                          htmlFor={`role-${roleOption.value}`}
                          className={`text-sm font-semibold capitalize cursor-pointer ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {roleOption.label}
                        </label>
                        <input
                          id={`role-${roleOption.value}`}
                          name={`role-${roleOption.value}`}
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {}} // Handled by parent div onClick
                          className={`h-4 w-4 text-mirsal-green focus:ring-mirsal-green rounded ${
                            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                          } ${
                            theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
                          }`}
                        />
                      </div>
                      <p className={`mt-1 text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {roleOption.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {allErrors.roles && (
            <p className="mt-2 text-sm text-red-500">{allErrors.roles}</p>
          )}
          <div className={`mt-3 p-3 rounded-lg text-xs ${
            theme === 'dark' ? 'bg-gray-800/50 text-gray-400' : 'bg-gray-50 text-gray-600'
          }`}>
            <p className="mt-1"><strong className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Multi-Role Support:</strong> Users can have multiple roles. Admins can combine admin role with other roles (host, guest, usher) to access different dashboards.</p>
          </div>
        </div>
      </div>

      {/* User Status */}
      <div className="space-y-4">
        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>User Status</h3>
        
        <div
          onClick={() => {
            setFormData(prev => ({ ...prev, is_active: !prev.is_active }));
          }}
          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
            formData.is_active
              ? theme === 'dark'
                ? 'bg-green-900/20 border-green-800 text-green-400 ring-2 ring-green-800/50'
                : 'bg-green-50 border-green-200 text-green-700 ring-2 ring-green-200/50'
              : theme === 'dark'
              ? 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`flex-shrink-0 ${
                formData.is_active
                  ? theme === 'dark' ? 'text-green-400' : 'text-green-600'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`}>
                {formData.is_active ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <XCircle className="h-5 w-5" />
                )}
              </div>
              <div>
                <label
                  htmlFor="is_active"
                  className={`text-sm font-semibold cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {formData.is_active ? 'Active User' : 'Inactive User'}
                </label>
                <p className={`mt-0.5 text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {formData.is_active 
                    ? 'User can log in and access the system' 
                    : 'User account is disabled and cannot log in'}
                </p>
              </div>
            </div>
            <div className="relative">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleInputChange}
                className={`h-5 w-5 text-mirsal-green focus:ring-mirsal-green rounded cursor-pointer ${
                  theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Security - Only show for new users */}
      {!isEdit && (
        <div className="space-y-4">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Security</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label htmlFor="password" className={`block text-sm font-medium mb-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Password *
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                error={allErrors.password}
                placeholder="Enter password"
                inputSize="lg"
                className="w-full h-14"
              />
            </div>

            <div className="w-full">
              <label htmlFor="password_confirm" className={`block text-sm font-medium mb-1 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Confirm Password *
              </label>
              <Input
                id="password_confirm"
                name="password_confirm"
                type="password"
                value={formData.password_confirm}
                onChange={handleInputChange}
                error={allErrors.password_confirm}
                placeholder="Confirm password"
                inputSize="lg"
                className="w-full h-14"
              />
            </div>
          </div>
        </div>
      )}


      {/* Form Actions */}
      <div className={`flex items-center justify-end space-x-3 pt-6 border-t ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        
        <Button
          type="submit"
          disabled={loading}
          className="min-w-[100px]"
        >
          {loading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEdit ? 'Updating...' : 'Creating...'}
            </div>
          ) : (
            isEdit ? 'Update User' : 'Create User'
          )}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
